import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { 
  Container, Typography, Box, Paper, Button, TextField, 
  Dialog, DialogActions, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Grid, InputAdornment, FormControl,
  InputLabel, Select, MenuItem, FormHelperText, Snackbar, Alert,
  CircularProgress, Stack, Tabs, Tab
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Share as ShareIcon, PersonAdd as PersonAddIcon, RemoveCircle as RemoveCircleIcon, PersonRemove as PersonRemoveIcon, Email as EmailIcon, Phone as PhoneIcon, Search as SearchIcon, Clear as ClearIcon, Undo as UndoIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useAuthStore } from '../../store/authStore';
import axios from 'axios';
import { useSelector } from 'react-redux';
import SelectUsersDialog from '../../components/admin/SelectUsersDialog';
import AdminLayout from '../../components/layouts/AdminLayout';
import { formatDate } from '../../utils/dateUtils';

const Coupons = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [coupons, setCoupons] = useState([]);
  const [usedCoupons, setUsedCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsedCoupons, setLoadingUsedCoupons] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0); // 0 - купоны, 1 - использованные купоны
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    value: 500,
    type: 'custom',
    expiresAt: null,
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [currentCouponId, setCurrentCouponId] = useState(null);
  const [selectUsersDialogOpen, setSelectUsersDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  
  // Состояния для поиска использованных купонов
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsedCoupons, setFilteredUsedCoupons] = useState([]);
  
  // Состояния для диалогов подтверждения
  const [confirmDistributeDialogOpen, setConfirmDistributeDialogOpen] = useState(false);
  const [confirmRevokeDialogOpen, setConfirmRevokeDialogOpen] = useState(false);
  const [confirmCancelUseDialogOpen, setConfirmCancelUseDialogOpen] = useState(false);
  const [couponToAction, setCouponToAction] = useState(null);
  
  const { user: userInfo } = useAuthStore();

  useEffect(() => {
    fetchCoupons();
    fetchUsedCoupons();
  }, []);
  
  // Эффект для фильтрации использованных купонов при изменении поискового запроса
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsedCoupons(usedCoupons);
      return;
    }
    
    const query = searchQuery.toLowerCase().trim();
    const filtered = usedCoupons.filter(userCoupon => {
      // Поиск по имени купона
      const couponName = userCoupon.coupon?.name?.toLowerCase() || '';
      // Поиск по имени пользователя
      const userName = userCoupon.user?.name?.toLowerCase() || '';
      // Поиск по email пользователя
      const userEmail = userCoupon.user?.email?.toLowerCase() || '';
      // Поиск по телефону пользователя
      const userPhone = userCoupon.user?.phone?.toLowerCase() || '';
      
      return couponName.includes(query) || 
             userName.includes(query) || 
             userEmail.includes(query) || 
             userPhone.includes(query);
    });
    
    setFilteredUsedCoupons(filtered);
  }, [searchQuery, usedCoupons]);
  
  const fetchUsedCoupons = async () => {
    try {
      setLoadingUsedCoupons(true);
      console.log('Fetching used coupons for admin...');
      const { data } = await axios.get('/api/coupons/used-coupons', {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      console.log('Received used coupons data:', data);
      const couponsData = Array.isArray(data) ? data : [];
      setUsedCoupons(couponsData);
      setFilteredUsedCoupons(couponsData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch used coupons');
      console.error('Error fetching used coupons:', err);
      showSnackbar('Не удалось загрузить использованные купоны', 'error');
    } finally {
      setLoadingUsedCoupons(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      console.log('Fetching coupons for admin...');
      const { data } = await axios.get('/api/coupons/admin', {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      console.log('Received coupons data:', data);
      setCoupons(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch coupons');
      console.error('Error fetching coupons:', err);
      showSnackbar('Не удалось загрузить купоны', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      name: '',
      description: '',
      value: 500,
      type: 'custom',
      expiresAt: null,
      isActive: true
    });
    setFormErrors({});
    setEditMode(false);
    setCurrentCouponId(null);
    setOpenDialog(true);
  };
  
  const handleEditCoupon = (coupon) => {
    setFormData({
      name: coupon.name,
      description: coupon.description || '',
      value: coupon.value,
      type: coupon.type,
      expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt) : null,
      isActive: coupon.isActive
    });
    setFormErrors({});
    setEditMode(true);
    setCurrentCouponId(coupon.id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Название обязательно';
    if (!formData.value || formData.value <= 0) errors.value = 'Введите корректную сумму';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      expiresAt: date
    });
  };

  // Открыть диалог подтверждения распределения купона
  const handleOpenDistributeConfirm = (coupon) => {
    setCouponToAction(coupon);
    setConfirmDistributeDialogOpen(true);
  };
  
  // Фактическое распределение купона всем пользователям
  const handleDistributeCoupon = async () => {
    if (!couponToAction) return;
    
    // Защита от множественных нажатий
    if (loading) {
      console.log('Операция уже выполняется, пожалуйста подождите');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await axios.post(`/api/coupons/distribute/${couponToAction.id}`, {}, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      
      showSnackbar(response.data.message, 'success');
      fetchCoupons();
    } catch (err) {
      console.error('Error distributing coupon:', err);
      setError(err.response?.data?.message || 'Failed to distribute coupon');
      showSnackbar('Не удалось распределить купон', 'error');
    } finally {
      setLoading(false);
      setConfirmDistributeDialogOpen(false);
      setCouponToAction(null);
    }
  };
  
  const handleOpenSelectUsersDialog = (coupon) => {
    setSelectedCoupon(coupon);
    setSelectUsersDialogOpen(true);
  };
  
  const handleCloseSelectUsersDialog = () => {
    setSelectUsersDialogOpen(false);
    setSelectedCoupon(null);
  };
  
  const handleDistributeToSelectedUsers = async (selectedUserIds) => {
    // Защита от множественных нажатий
    if (loading) {
      console.log('Операция уже выполняется, пожалуйста подождите');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await axios.post(`/api/coupons/distribute-selected/${selectedCoupon.id}`, {
        userIds: selectedUserIds
      }, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      
      showSnackbar(response.data.message, 'success');
      handleCloseSelectUsersDialog();
      fetchCoupons();
    } catch (err) {
      console.error('Error distributing coupon to selected users:', err);
      setError(err.response?.data?.message || 'Failed to distribute coupon');
      showSnackbar('Не удалось распределить купон выбранным пользователям', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Открыть диалог подтверждения отзыва купона
  const handleOpenRevokeConfirm = (coupon) => {
    setCouponToAction(coupon);
    setConfirmRevokeDialogOpen(true);
  };
  
  // Фактический отзыв купона у всех пользователей
  const handleRevokeCoupon = async () => {
    if (!couponToAction) return;
    
    try {
      setLoading(true);
      
      const response = await axios.post(`/api/coupons/${couponToAction.id}/revoke`, {}, {
        headers: { Authorization: `Bearer ${userInfo.token}` }
      });
      
      showSnackbar('Купон успешно отозван у всех пользователей', 'success');
      fetchCoupons();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || 'Ошибка при отзыве купона',
        'error'
      );
    } finally {
      setConfirmRevokeDialogOpen(false);
      setCouponToAction(null);
    }
  };
  
  // Функция для отмены использования купона
  const handleOpenCancelUseDialog = (userCoupon) => {
    setCouponToAction(userCoupon);
    setConfirmCancelUseDialogOpen(true);
  };
  
  const handleCancelUsedCoupon = async () => {
    try {
      await axios.post(`/api/coupons/used-coupons/${couponToAction.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${userInfo.token}` }
      });
      
      showSnackbar('Использование купона успешно отменено', 'success');
      fetchUsedCoupons(); // Обновляем список использованных купонов
      fetchCoupons(); // Обновляем список всех купонов
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || 'Ошибка при отмене использования купона',
        'error'
      );
    } finally {
      setConfirmCancelUseDialogOpen(false);
      setCouponToAction(null);
    }
  };
  
  const handleRevokeFromSelectedUsers = async (selectedUserIds) => {
    try {
      setLoading(true);
      
      const response = await axios.post(`/api/coupons/revoke-selected/${selectedCoupon.id}`, {
        userIds: selectedUserIds
      }, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      
      showSnackbar(response.data.message, 'success');
      handleCloseSelectUsersDialog();
      fetchCoupons();
    } catch (err) {
      console.error('Error revoking coupon from selected users:', err);
      setError(err.response?.data?.message || 'Failed to revoke coupon');
      showSnackbar('Не удалось отозвать купон у выбранных пользователей', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      if (editMode) {
        // Редактирование существующего купона
        await axios.put(`/api/coupons/${currentCouponId}`, formData, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        
        handleCloseDialog();
        fetchCoupons();
        showSnackbar('Купон успешно обновлен', 'success');
      } else {
        // Создание нового купона
        await axios.post('/api/coupons', formData, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        
        handleCloseDialog();
        fetchCoupons();
        showSnackbar('Купон успешно создан', 'success');
      }
    } catch (err) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} coupon:`, err);
      showSnackbar(
        err.response?.data?.message || 
        `Ошибка при ${editMode ? 'обновлении' : 'создании'} купона`, 
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
          <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Управление купонами
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{ mt: { xs: 2, sm: 0 } }}
          >
            Создать купон
          </Button>
        </Box>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            aria-label="купоны и использованные купоны"
            variant={isMobile ? "fullWidth" : "standard"}
          >
            <Tab label="Все купоны" id="tab-0" aria-controls="tabpanel-0" />
            <Tab label="Использованные купоны" id="tab-1" aria-controls="tabpanel-1" />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Вкладка со всеми купонами */}
        <div
          role="tabpanel"
          hidden={activeTab !== 0}
          id="tabpanel-0"
          aria-labelledby="tab-0"
        >
          {activeTab === 0 && (
            <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: { xs: 1, sm: 2 } }}>
              <TableContainer sx={{ 
                maxHeight: 600,
                width: '100%',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.primary.light,
                  borderRadius: 4
                }
              }}>
                <Table stickyHeader size={isMobile ? 'small' : 'medium'} sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, width: '15%' }}>Название</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' }, width: '20%' }}>Описание</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, width: '10%' }}>Сумма (₽)</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' }, width: '10%' }}>Тип</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' }, width: '15%' }}>Срок действия</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, width: '10%' }}>Статус</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' }, width: '15%' }}>Дата создания</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, width: '15%' }}>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">Загрузка...</TableCell>
                      </TableRow>
                    ) : coupons.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">Нет доступных купонов</TableCell>
                      </TableRow>
                    ) : (
                      coupons.map((coupon) => (
                        <TableRow key={coupon.id}>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 80, sm: 150 } }}>
                              {coupon.name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: { md: 150, lg: 200 } }}>
                              {coupon.description || 'Нет описания'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>{coupon.value} ₽</TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                            {coupon.type === 'welcome' ? 'Приветственный' : 'Обычный'}
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                            {coupon.expiresAt ? formatDate(coupon.expiresAt) : 'Бессрочно'}
                          </TableCell>
                          {(() => {
                            const isExpired = coupon.expiresAt ? new Date(coupon.expiresAt) <= new Date() : false;
                            if (!coupon.isActive) {
                              return (
                                <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                                  <Box sx={{ color: 'error.main', fontWeight: 'medium' }}>Неактивен</Box>
                                </TableCell>
                              );
                            }
                            if (isExpired) {
                              return (
                                <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                                  <Box sx={{ color: 'error.main', fontWeight: 'medium' }}>Просрочен</Box>
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                                <Box sx={{ color: 'success.main', fontWeight: 'medium' }}>Активен</Box>
                              </TableCell>
                            );
                          })()}
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                            {formatDate(coupon.createdAt)}
                          </TableCell>
                          <TableCell align="right" sx={{ p: { xs: 0.5, sm: 1 }, whiteSpace: 'nowrap' }}>
                            {isMobile ? (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.5 }}>
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={() => handleEditCoupon(coupon)}
                                  sx={{ p: 0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="secondary" 
                                  onClick={() => handleOpenDistributeConfirm(coupon)}
                                  sx={{ p: 0.5 }}
                                  disabled={loading}
                                >
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="info" 
                                  onClick={() => handleOpenSelectUsersDialog(coupon)}
                                  sx={{ p: 0.5 }}
                                  disabled={loading}
                                >
                                  <PersonAddIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={() => handleOpenRevokeConfirm(coupon)}
                                  sx={{ p: 0.5 }}
                                >
                                  <RemoveCircleIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="warning" 
                                  onClick={() => handleOpenSelectUsersDialog({...coupon, isRevoking: true})}
                                  sx={{ p: 0.5 }}
                                >
                                  <PersonRemoveIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Tooltip title="Редактировать купон" arrow>
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    onClick={() => handleEditCoupon(coupon)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Распределить всем пользователям" arrow>
                                  <IconButton
                                    size="small"
                                    color="secondary" 
                                    onClick={() => handleOpenDistributeConfirm(coupon)}
                                    disabled={loading}
                                  >
                                    <ShareIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Распределить выбранным пользователям" arrow>
                                  <IconButton 
                                    size="small" 
                                    color="info" 
                                    onClick={() => handleOpenSelectUsersDialog(coupon)}
                                    disabled={loading}
                                  >
                                    <PersonAddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Отозвать у всех пользователей" arrow>
                                  <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => handleOpenRevokeConfirm(coupon)}
                                  >
                                    <RemoveCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Отозвать у выбранных пользователей" arrow>
                                  <IconButton 
                                    size="small" 
                                    color="warning" 
                                    onClick={() => handleOpenSelectUsersDialog({...coupon, isRevoking: true})}
                                  >
                                    <PersonRemoveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </div>

        {/* Вкладка с использованными купонами */}
        <div
          role="tabpanel"
          hidden={activeTab !== 1}
          id="tabpanel-1"
          aria-labelledby="tab-1"
        >
          {activeTab === 1 && (
            <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: { xs: 1, sm: 2 } }}>
              {/* Поле поиска для использованных купонов */}
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Поиск по имени, email или телефону"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton edge="start">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setSearchQuery('')}
                          aria-label="очистить поиск"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              <TableContainer sx={{ 
                maxHeight: 600,
                width: '100%',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.primary.light,
                  borderRadius: 4
                }
              }}>
                <Table stickyHeader size={isMobile ? 'small' : 'medium'} sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, width: '15%' }}>Купон</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' }, width: '15%' }}>Описание</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, width: '10%' }}>Сумма (₽)</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' }, width: '15%' }}>Пользователь</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' }, width: '15%' }}>Контакты</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, width: '15%' }}>Дата использования</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' }, width: '10%' }}>Подтвердил</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, width: '5%' }}>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingUsedCoupons ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">Загрузка...</TableCell>
                      </TableRow>
                    ) : filteredUsedCoupons.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          {searchQuery ? 'Не найдено купонов, соответствующих поиску' : 'Нет использованных купонов'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsedCoupons.map((userCoupon) => (
                        <TableRow key={userCoupon.id}>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            {userCoupon.coupon?.name || 'Неизвестный купон'}
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                            <Tooltip title={userCoupon.coupon?.description || ''}>
                              <Typography noWrap variant="body2">
                                {userCoupon.coupon?.description || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            {userCoupon.coupon?.value || '-'}
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                            {userCoupon.user?.name || 'Неизвестный пользователь'}
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                            <Stack spacing={1}>
                              {userCoupon.user?.email && (
                                <Typography variant="body2" noWrap>
                                  <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                  {userCoupon.user.email}
                                </Typography>
                              )}
                              {userCoupon.user?.phone && (
                                <Typography variant="body2" noWrap>
                                  <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                  {userCoupon.user.phone}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            {userCoupon.usedAt ? new Date(userCoupon.usedAt).toLocaleString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                            {userCoupon.verifiedByAdmin?.name || '-'}
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            <Tooltip title="Отменить использование">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenCancelUseDialog(userCoupon)}
                              >
                                <UndoIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </div>
        {/* Create/Edit Coupon Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            p: { xs: 2, sm: 3 },
            bgcolor: isMobile ? 'primary.main' : 'transparent',
            color: isMobile ? 'white' : 'text.primary'
          }}>
            {editMode ? 'Редактировать купон' : 'Создать новый купон'}
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Название купона"
                  fullWidth
                  value={formData.name}
                  onChange={handleChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Описание"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="value"
                  label="Сумма"
                  type="number"
                  fullWidth
                  value={formData.value}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">₽</InputAdornment>,
                  }}
                  error={!!formErrors.value}
                  helperText={formErrors.value}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Тип купона</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    label="Тип купона"
                  >
                    <MenuItem value="welcome">Приветственный</MenuItem>
                    <MenuItem value="custom">Обычный</MenuItem>
                  </Select>
                  <FormHelperText>
                    {formData.type === 'custom' 
                      ? 'Будет добавлен всем активным пользователям, включая администраторов' 
                      : 'Будет выдаваться новым пользователям при регистрации'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Срок действия (оставьте пустым для бессрочного)"
                    value={formData.expiresAt}
                    onChange={handleDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    clearable
                  />
                </LocalizationProvider>
              </Grid>
              
              {editMode && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Статус купона</InputLabel>
                    <Select
                      name="isActive"
                      value={formData.isActive}
                      onChange={handleChange}
                      label="Статус купона"
                    >
                      <MenuItem value={true}>Активен</MenuItem>
                      <MenuItem value={false}>Неактивен</MenuItem>
                    </Select>
                    <FormHelperText>
                      Неактивные купоны не могут быть использованы клиентами
                    </FormHelperText>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
            <Button 
              onClick={handleCloseDialog} 
              size={isMobile ? "large" : "medium"}
              fullWidth={isMobile}
              variant={isMobile ? "outlined" : "text"}
            >
              Отмена
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              size={isMobile ? "large" : "medium"}
              fullWidth={isMobile}
              sx={{ ml: isMobile ? 0 : 1 }}
            >
              {editMode ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogActions>
        </Dialog>

{/* Snackbar for notifications */}
<Snackbar
  open={snackbar.open}
  autoHideDuration={6000}
  onClose={handleCloseSnackbar}
  anchorOrigin={{ 
    vertical: isMobile ? 'bottom' : 'bottom', 
    horizontal: isMobile ? 'center' : 'right' 
  }}
  sx={{ 
    bottom: { xs: 16, sm: 24 },
    width: { xs: '90%', sm: 'auto' },
    left: { xs: '5%', sm: 'auto' }
  }}
>
  <Alert 
    onClose={handleCloseSnackbar} 
    severity={snackbar.severity} 
    sx={{ 
      width: '100%',
      boxShadow: 3
    }}
    variant="filled"
  >
    {snackbar.message}
  </Alert>
</Snackbar>

      {/* Диалог выбора пользователей для распределения или отзыва купона */}
      {selectedCoupon && (
        <SelectUsersDialog
          open={selectUsersDialogOpen}
          onClose={handleCloseSelectUsersDialog}
          onSubmit={selectedCoupon.isRevoking ? handleRevokeFromSelectedUsers : handleDistributeToSelectedUsers}
          couponId={selectedCoupon.id}
          couponName={selectedCoupon.name}
          isRevoking={selectedCoupon.isRevoking}
        />
      )}

      {/* Диалог подтверждения распределения купона всем пользователям */}
      <Dialog
        open={confirmDistributeDialogOpen}
        onClose={() => !loading && setConfirmDistributeDialogOpen(false)}
        aria-labelledby="confirm-distribute-dialog-title"
        disableEscapeKeyDown={loading}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-distribute-dialog-title" sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          p: { xs: 2, sm: 3 }
        }}>
          Подтверждение распределения купона
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Купон: <strong>{couponToAction?.name}</strong>
            </Typography>
            <Typography variant="body1" gutterBottom>
              Вы уверены, что хотите распределить этот купон всем активным пользователям?
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                • Купон будет добавлен только тем пользователям, у которых его еще нет<br />
                • Всем пользователям будет отправлено уведомление по электронной почте<br />
                • Операция может занять некоторое время
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <Button 
            onClick={() => setConfirmDistributeDialogOpen(false)} 
            color="primary"
            disabled={loading}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            variant={isMobile ? "outlined" : "text"}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleDistributeCoupon} 
            color="primary" 
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            sx={{ ml: isMobile ? 0 : 1 }}
          >
            {loading ? 'Распределение...' : 'Подтвердить'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Диалог подтверждения отзыва купона у всех пользователей */}
      <Dialog
        open={confirmRevokeDialogOpen}
        onClose={() => setConfirmRevokeDialogOpen(false)}
        aria-labelledby="confirm-revoke-dialog-title"
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-revoke-dialog-title" sx={{ 
          bgcolor: 'error.main', 
          color: 'white',
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          p: { xs: 2, sm: 3 }
        }}>
          Подтверждение отзыва купона
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography sx={{ mt: 1 }}>
            Вы уверены, что хотите отозвать купон "{couponToAction?.name}" у всех пользователей?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'medium' }}>
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <Button 
            onClick={() => setConfirmRevokeDialogOpen(false)} 
            color="primary"
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            variant={isMobile ? "outlined" : "text"}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleRevokeCoupon} 
            color="error" 
            variant="contained"
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            sx={{ ml: isMobile ? 0 : 1 }}
          >
            Отозвать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Cancel Use Dialog */}
      <Dialog
        open={confirmCancelUseDialogOpen}
        onClose={() => setConfirmCancelUseDialogOpen(false)}
        aria-labelledby="cancel-use-dialog-title"
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="cancel-use-dialog-title" sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          p: { xs: 2, sm: 3 }
        }}>
          Подтверждение отмены использования купона
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {couponToAction && (
            <>
              <Typography sx={{ mt: 1 }}>
                Вы уверены, что хотите отменить использование этого купона?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Купон:</strong> {couponToAction.coupon?.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Пользователь:</strong> {couponToAction.user?.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Дата использования:</strong> {couponToAction.usedAt && new Date(couponToAction.usedAt).toLocaleString('ru-RU')}
                </Typography>
              </Box>
              <Typography variant="body2" color="primary" sx={{ mt: 2, fontWeight: 'medium' }}>
                После отмены купон снова станет доступным для использования.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <Button 
            onClick={() => setConfirmCancelUseDialogOpen(false)} 
            color="inherit"
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            variant={isMobile ? "outlined" : "text"}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleCancelUsedCoupon} 
            color="primary" 
            variant="contained"
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            sx={{ ml: isMobile ? 0 : 1 }}
          >
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>

      </Paper>
    </Container>
);
};

export default Coupons;
